import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../hooks/useAuth';
import api from '../lib/api';
import TrackFlowLogo from '../components/TrackFlowLogo';

const loginSchema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

const forgotSchema = z.object({
  forgotEmail: z.string().email('Enter a valid email address'),
});

export default function Login() {
  const { login, user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [showForgot, setShowForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(loginSchema) });

  const {
    register: registerForgot,
    handleSubmit: handleForgotSubmit,
    formState: { errors: forgotErrors, isSubmitting: forgotSubmitting },
  } = useForm({ resolver: zodResolver(forgotSchema) });

  useEffect(() => {
    if (!isLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, isLoading, navigate]);

  const onSubmit = async ({ email, password }) => {
    try {
      await login(email, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const message =
        err?.response?.data?.error || 'Invalid email or password. Please try again.';
      setError('root', { message });
    }
  };

  const onForgotSubmit = async ({ forgotEmail }) => {
    await api.post('/api/auth/forgot-password', { email: forgotEmail });
    setForgotSent(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <TrackFlowLogo size="lg" showText textDark className="mb-2" />
          <p className="text-sm text-gray-500 mt-1">Sign in to your account</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          {!showForgot ? (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
              {errors.root && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                  {errors.root.message}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  {...register('email')}
                  className={`w-full px-3 py-2.5 rounded-lg border text-sm bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.email ? 'border-red-400' : 'border-gray-300 hover:border-gray-400'
                  }`}
                  placeholder="you@company.com"
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgot(true)}
                    className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  {...register('password')}
                  className={`w-full px-3 py-2.5 rounded-lg border text-sm bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.password ? 'border-red-400' : 'border-gray-300 hover:border-gray-400'
                  }`}
                  placeholder="Enter your password"
                />
                {errors.password && (
                  <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Signing in...
                  </span>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>
          ) : (
            <div>
              {!forgotSent ? (
                <form onSubmit={handleForgotSubmit(onForgotSubmit)} className="space-y-5" noValidate>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900 mb-1">Reset your password</h2>
                    <p className="text-sm text-gray-500">Enter your email and we'll send you a reset link.</p>
                  </div>

                  <div>
                    <label htmlFor="forgotEmail" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Email address
                    </label>
                    <input
                      id="forgotEmail"
                      type="email"
                      autoComplete="email"
                      {...registerForgot('forgotEmail')}
                      className={`w-full px-3 py-2.5 rounded-lg border text-sm bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        forgotErrors.forgotEmail ? 'border-red-400' : 'border-gray-300 hover:border-gray-400'
                      }`}
                      placeholder="you@company.com"
                    />
                    {forgotErrors.forgotEmail && (
                      <p className="mt-1 text-xs text-red-600">{forgotErrors.forgotEmail.message}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={forgotSubmitting}
                    className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {forgotSubmitting ? 'Sending...' : 'Send reset link'}
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowForgot(false)}
                    className="w-full text-sm text-gray-500 hover:text-gray-700"
                  >
                    Back to sign in
                  </button>
                </form>
              ) : (
                <div className="text-center space-y-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Check your email</p>
                    <p className="text-sm text-gray-500 mt-1">If that email exists, a reset link has been sent.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setShowForgot(false); setForgotSent(false); }}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Back to sign in
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          Don't have an account?{' '}
          <span className="text-gray-400">Contact your administrator.</span>
        </p>
        <p className="text-center text-xs text-gray-400 mt-2">
          {import.meta.env.VITE_APP_NAME || 'TrackFlow'} — Employee Time Tracking
        </p>
      </div>
    </div>
  );
}
